import {
  SKETCH_EXTENT, type FloorPlanSketch, type SketchInk, type SketchPoint, type SketchShape,
} from '../models/floorplan-sketch';
import {
  contoursOf, fillPolygon, simplifiedRing, strokeOutline, strokeSegment, type Bitmap, type Pixel,
} from './raster';
import { DEFAULT_TRACE_CONFIG, type TraceConfig } from './trace-config';

/**
 * What a plan made from an image keeps of the image as it is drawn.
 *
 * Whatever is marked in the review — a desk, a wall, a door, a window, a piece of furniture, a
 * fitting, a label with its words typed in — is replaced on the plan by the editor's own object.
 * Everything else on the drawing stays exactly as drawn: a meeting table with its chairs round
 * it that nobody marked, a curved wall, a counter, a plant, writing nobody typed in. Each piece of
 * that ink is kept as the outline of its ink, holes and all, so its shape is the drawing's own.
 *
 * Worked out when the plan is made, from what the review ends with: a find taken away in the
 * review leaves its ink to be kept as drawn, and one marked by hand takes the ink it covers.
 * Rooms and the building's outline replace no ink: they are the spaces the walls close off.
 */

/**
 * A desk's size when the sketch does not give one, in the units of `x`: what the review gives a
 * desk it cannot size (`deskSizeOf`). Every desk the review hands on is sized; this is for others.
 */
const UNSIZED_DESK = { length: 40, breadth: 20 };

/**
 * How much looser the outlines are drawn, each time, when the drawing has more to keep than the
 * points allowed: so every piece is kept, a little less exactly, rather than some not at all.
 */
const LOOSER = 2;

/** As loose as an outline is ever drawn, in pixels; past it, the smallest pieces are left out. */
const LOOSEST_PIXELS = 4;

/** Where a sketch point is on the ink, in pixels. */
const pixelOf = (ink: SketchInk, point: SketchPoint): Pixel => [
  (point.x / SKETCH_EXTENT) * ink.width,
  (point.y / SKETCH_EXTENT) * ink.height,
];

/** A length in the units of the sketch's `x`, in pixels: the same in every direction. */
const pixelsOf = (ink: SketchInk, length: number): number => (length / SKETCH_EXTENT) * ink.width;

const sketchPointOf = (ink: SketchInk, [x, y]: Pixel): SketchPoint => ({
  x: Math.round((x / ink.width) * SKETCH_EXTENT * 100) / 100,
  y: Math.round((y / ink.height) * SKETCH_EXTENT * 100) / 100,
});

/** The ink each find on the plan stands for: its shape, and its drawn outline round it. */
const coveredBy = (ink: SketchInk, sketch: FloorPlanSketch, reach: number): Bitmap => {
  const covered: Bitmap = { width: ink.width, height: ink.height, data: new Uint8Array(ink.width * ink.height) };
  const cover = (outline: readonly Pixel[]): void => {
    fillPolygon(covered, outline);
    strokeOutline(covered, outline, reach);
  };
  sketch.desks.forEach((desk) => {
    const [cx, cy] = pixelOf(ink, desk);
    // The plan's angle counts up from across, counter-clockwise; the picture's y runs down.
    const radians = (-desk.rotationDegrees * Math.PI) / 180;
    const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
    const halfLength = pixelsOf(ink, desk.width ?? UNSIZED_DESK.length) / 2;
    const halfBreadth = pixelsOf(ink, desk.depth ?? UNSIZED_DESK.breadth) / 2;
    cover([[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([su, sv]): Pixel => [
      cx + (su * halfLength * ux) - (sv * halfBreadth * uy),
      cy + (su * halfLength * uy) + (sv * halfBreadth * ux),
    ]));
  });

  sketch.walls.forEach((wall) => {
    const half = wall.thickness ? pixelsOf(ink, wall.thickness) / 2 : 0;
    strokeSegment(covered, pixelOf(ink, wall.from), pixelOf(ink, wall.to), half + reach);
  });

  (sketch.areas ?? []).forEach((area) => cover(area.outline.map((point) => pixelOf(ink, point))));

  // Writing with its words typed in is replaced by them; writing nobody typed in stays as written.
  sketch.labels.filter((label) => label.text.trim() && label.box).forEach((label) => {
    const box = label.box as NonNullable<typeof label.box>;
    cover([
      pixelOf(ink, { x: box.left, y: box.top }), pixelOf(ink, { x: box.right, y: box.top }),
      pixelOf(ink, { x: box.right, y: box.bottom }), pixelOf(ink, { x: box.left, y: box.bottom }),
    ]);
  });

  return covered;
};

/**
 * The pieces of the drawing nothing on the plan replaces, each the exact outline of its ink and
 * of the holes in it, largest first. Specks are left out. When they would take more than
 * `maxPoints` between them, every outline is drawn looser, not some left out — the largest piece
 * is often the walls themselves — and only past the loosest are the smallest pieces dropped.
 */
export const asDrawnShapesOf = (
  ink: SketchInk,
  sketch: FloorPlanSketch,
  config: TraceConfig['asDrawn'] = DEFAULT_TRACE_CONFIG.asDrawn,
): SketchShape[] => {
  const reach = (ink.stroke * config.coverStrokes) + 1;
  const covered = coveredBy(ink, sketch, reach);
  const left: Bitmap = { width: ink.width, height: ink.height, data: new Uint8Array(ink.data.length) };
  for (let index = 0; index < ink.data.length; index += 1) {
    if (ink.data[index] && !covered.data[index]) left.data[index] = 1;
  }

  const speck = Math.max(2, ink.stroke * config.speckStrokes);
  const pieces = contoursOf(left)
    .filter((piece) => Math.max(piece.width, piece.height) >= speck)
    .sort((first, second) => second.area - first.area);

  /** Every piece's rings, kept within `tolerance` of the ink; a ring too small to keep left out. */
  const outlinesAt = (tolerance: number): Pixel[][][] => pieces.flatMap((piece) => {
    const simplified = (ring: Pixel[]): Pixel[] => {
      const open = simplifiedRing(ring.slice(0, -1), tolerance);
      return open.length >= 3 ? [...open, open[0]] : [];
    };
    const outer = simplified(piece.outer);
    return outer.length ? [[outer, ...piece.holes.map(simplified).filter((ring) => ring.length)]] : [];
  });
  const pointsIn = (outlines: Pixel[][][]): number => outlines
    .reduce((sum, rings) => sum + rings.reduce((count, ring) => count + ring.length, 0), 0);

  let tolerance = config.tolerancePixels;
  let outlines = outlinesAt(tolerance);
  while (pointsIn(outlines) > config.maxPoints && tolerance < LOOSEST_PIXELS) {
    tolerance = Math.min(LOOSEST_PIXELS, tolerance * LOOSER);
    outlines = outlinesAt(tolerance);
  }

  // Still too many at the loosest: the smallest pieces go, the largest first kept.
  const shapes: SketchShape[] = [];
  let points = 0;
  outlines.forEach((rings) => {
    const count = rings.reduce((sum, ring) => sum + ring.length, 0);
    if (points + count > config.maxPoints) return;
    points += count;
    shapes.push({ rings: rings.map((ring) => ring.map((pixel) => sketchPointOf(ink, pixel))) });
  });

  return shapes;
};

export default asDrawnShapesOf;
